import { PREFERENCE_TOPICS, type NotificationPreferences, type PreferenceTopic } from 'types/engagement.types';

/** Pure rules of the customer notification UI (bell, list, settings). */

type Matrix = NotificationPreferences['matrix'];
type PrefChannel = 'notification' | 'email';

/** Badge content: hidden at 0, capped at 99+. */
export const badgeLabel = (count: number): string | null => (count <= 0 ? null : count > 99 ? '99+' : String(count));

export const relativeTime = (iso: string, lng: string, now = new Date()): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
  const locale = lng.startsWith('en') ? 'en-GB' : 'de-DE';
  const time = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(d);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (days === 0) return `${rtf.format(0, 'day')}, ${time}`;
  if (days === 1) return rtf.format(-1, 'day');
  return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit' }).format(d);
};

export const DEFAULT_MATRIX: Matrix = {
  account: { notification: true, email: true },
  campaigns: { notification: true, email: true },
  daily_report: { notification: true, email: true },
  news: { notification: true, email: false },
  offers: { notification: true, email: false },
};

/** "Konto und Sicherheit" cannot be switched off; offers by e-mail need the marketing consent. */
export const isCellLocked = (topic: PreferenceTopic, channel: PrefChannel, marketingConsent: boolean | undefined): boolean =>
  topic === 'account' || (topic === 'offers' && channel === 'email' && marketingConsent === false);

export const normalizeMatrix = (m: Partial<Matrix> | undefined): Matrix => {
  const out = { ...DEFAULT_MATRIX };
  PREFERENCE_TOPICS.forEach((k) => {
    if (m?.[k]) out[k] = { ...DEFAULT_MATRIX[k], ...m[k] };
  });
  out.account = { notification: true, email: true };
  return out;
};
